import { commonHeader, generateEndpoint } from "~/config/apiConfig";
import { ErrorBoundary } from "~/errors/errorBoundary";
import type {
  Document,
  UpdateDocumentContent,
  UpdateDocumentData,
  UpdateDocumentStatus,
} from "~/interface/document";

export class DocumentApi {
  private getRequestInfo(
    method: string,
    requestBody?: UpdateDocumentData | UpdateDocumentContent | UpdateDocumentStatus,
  ): RequestInit {
    return {
      method: method,
      headers: commonHeader,
      credentials: "include",
      keepalive: false,
      ...{
        body: JSON.stringify(requestBody),
      },
    };
  }

  async get(
    organization_id: string,
    project_id: string,
    document_id: string,
  ): Promise<Document | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/documents/${document_id}`,
    );

    try {
      const requestInfo = this.getRequestInfo("GET");

      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const document = await response.json();
        return document;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async getAllByType(
    organization_id: string,
    project_id: string,
    type: string,
  ): Promise<Document[] | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/documents?type=${type}`,
    );

    try {
      const requestInfo = this.getRequestInfo("GET");
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        const documents = await response.json();
        return documents;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async updateInfo(
    organization_id: string,
    project_id: string,
    id: string,
    updateData: UpdateDocumentData,
  ): Promise<Response | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/documents/${id}`,
    );
    const requestInfo = this.getRequestInfo("POST", updateData);

    try {
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        // toast.success("保存に成功しました"); // 保存ボタンの状態変化で明らかなため削除
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async updateContent(
    organization_id: string,
    project_id: string,
    id: string,
    updateData: UpdateDocumentContent,
  ): Promise<Response | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/documents/${id}/content`,
    );
    const requestInfo = this.getRequestInfo("POST", updateData);

    try {
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async updateStatus(
    organization_id: string,
    project_id: string,
    type: string,
    updateData: UpdateDocumentStatus,
  ): Promise<Response | null> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/documents/status`,
    );
    const requestBody = { ...updateData, type };
    const requestInfo = this.getRequestInfo("POST", requestBody);

    try {
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        return response;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return null;
      }
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  async delete(organization_id: string, project_id: string, id: string): Promise<boolean | string> {
    const deleteDocumentUrl = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/documents/${id}`,
    );
    try {
      const requestInfo = this.getRequestInfo("DELETE");
      const response = await fetch(deleteDocumentUrl, requestInfo);

      if (response.ok) {
        return true;
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.message || ErrorBoundary(response.status);
        console.error(errorMessage);
        return errorMessage;
      }
    } catch (error) {
      console.error(error);
      return "ネットワークエラーが発生しました";
    }
  }

  async rollback(organization_id: string, project_id: string, id: string): Promise<boolean> {
    const endpoint = generateEndpoint(
      `/organizations/${organization_id}/projects/${project_id}/documents/${id}/rollback`,
    );
    const requestInfo = this.getRequestInfo("POST");

    try {
      const response = await fetch(endpoint, requestInfo);

      if (response.ok) {
        return true;
      } else {
        const errorMessage = ErrorBoundary(response.status);
        console.error(errorMessage);
        return false;
      }
    } catch (error) {
      console.error(error);
      return false;
    }
  }
}
